import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Integration,
  CreateIntegrationDto,
  UpdateIntegrationDto,
  FindIntegrationsDto,
  IntegrationFilters,
  IntegrationStats,
  IntegrationsState,
  PaginatedResponse,
} from '../types/integration.types';

// Estado inicial
const initialState: IntegrationsState = {
  integrations: [],
  currentIntegration: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  filters: {
    search: '',
    type: undefined,
    status: undefined,
    isActive: undefined,
  },
  stats: null,
};

// Acciones síncronas (las sagas manejan la lógica asíncrona)
export const fetchIntegrations = (query: FindIntegrationsDto = {}) => ({
  type: 'integrations/fetchIntegrations',
  payload: query,
});

export const fetchIntegration = (id: string) => ({
  type: 'integrations/fetchIntegration',
  payload: { id },
});

export const createIntegration = (data: CreateIntegrationDto) => ({
  type: 'integrations/createIntegration',
  payload: { data },
});

export const updateIntegration = (id: string, data: UpdateIntegrationDto) => ({
  type: 'integrations/updateIntegration',
  payload: { id, data },
});

export const deleteIntegration = (id: string) => ({
  type: 'integrations/deleteIntegration',
  payload: { id },
});

export const toggleIntegrationStatus = (id: string) => ({
  type: 'integrations/toggleIntegrationStatus',
  payload: { id },
});

export const testConnection = (id: string) => ({
  type: 'integrations/testConnection',
  payload: { id },
});

export const fetchStats = () => ({
  type: 'integrations/fetchStats',
});

// Slice
const integrationsSlice = createSlice({
  name: 'integrations',
  initialState,
  reducers: {
    setFilters: (
      state,
      action: PayloadAction<Partial<IntegrationFilters>>
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        search: '',
        type: undefined,
        status: undefined,
        isActive: undefined,
      };
    },
    setCurrentIntegration: (state, action: PayloadAction<Integration | null>) => {
      state.currentIntegration = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    // Reducers para integraciones
    fetchIntegrationsStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchIntegrationsSuccess: (
      state,
      action: PayloadAction<PaginatedResponse<Integration>>
    ) => {
      state.loading = false;
      state.integrations = action.payload.data || [];
      state.pagination = {
        page: action.payload.page || 1,
        limit: action.payload.limit || 10,
        total: action.payload.total || 0,
        totalPages: action.payload.totalPages || 0,
      };
    },
    fetchIntegrationStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchIntegrationSuccess: (state, action: PayloadAction<Integration>) => {
      state.loading = false;
      state.currentIntegration = action.payload;
    },
    createIntegrationStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    createIntegrationSuccess: (state, action: PayloadAction<Integration>) => {
      state.loading = false;
      state.integrations.unshift(action.payload);
      state.pagination.total += 1;
    },
    updateIntegrationStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    updateIntegrationSuccess: (state, action: PayloadAction<Integration>) => {
      state.loading = false;
      const index = state.integrations.findIndex(
        (i) => i.id === action.payload.id
      );
      if (index !== -1) {
        state.integrations[index] = action.payload;
      }
      if (state.currentIntegration?.id === action.payload.id) {
        state.currentIntegration = action.payload;
      }
    },
    deleteIntegrationStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    deleteIntegrationSuccess: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.integrations = state.integrations.filter(
        (i) => i.id !== action.payload
      );
      if (state.currentIntegration?.id === action.payload) {
        state.currentIntegration = null;
      }
      state.pagination.total -= 1;
    },
    toggleIntegrationStatusStart: (state) => {
      state.error = null;
    },
    toggleIntegrationStatusSuccess: (state, action: PayloadAction<Integration>) => {
      const index = state.integrations.findIndex(
        (i) => i.id === action.payload.id
      );
      if (index !== -1) {
        state.integrations[index] = action.payload;
      }
      if (state.currentIntegration?.id === action.payload.id) {
        state.currentIntegration = action.payload;
      }
    },
    testConnectionStart: (state) => {
      state.error = null;
    },
    testConnectionSuccess: (state, action: PayloadAction<Integration>) => {
      const index = state.integrations.findIndex(
        (i) => i.id === action.payload.id
      );
      if (index !== -1) {
        state.integrations[index] = action.payload;
      }
      if (state.currentIntegration?.id === action.payload.id) {
        state.currentIntegration = action.payload;
      }
    },
    fetchStatsStart: (state) => {
      // No cambiar loading porque stats puede cargarse en paralelo
    },
    fetchStatsSuccess: (state, action: PayloadAction<IntegrationStats>) => {
      state.stats = action.payload;
    },
    integrationsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

export const {
  setFilters,
  clearFilters,
  setCurrentIntegration,
  clearError,
  fetchIntegrationsStart,
  fetchIntegrationsSuccess,
  fetchIntegrationStart,
  fetchIntegrationSuccess,
  createIntegrationStart,
  createIntegrationSuccess,
  updateIntegrationStart,
  updateIntegrationSuccess,
  deleteIntegrationStart,
  deleteIntegrationSuccess,
  toggleIntegrationStatusStart,
  toggleIntegrationStatusSuccess,
  testConnectionStart,
  testConnectionSuccess,
  fetchStatsStart,
  fetchStatsSuccess,
  integrationsFailure,
} = integrationsSlice.actions;

export default integrationsSlice.reducer;

